import { useId, useState } from 'react';
import { api, Alert, Badge, ErrorAlert, formatTime, relativeTime, Spinner, useAsyncAction } from '@pryme/ui';
import type { DomainChallenge, OrgDomain } from '../lib/types';
import { CopyField } from './CodeBlock';

const tone = (s: string) => (s === 'verified' ? 'ok' : s === 'failed' || s === 'stale' ? 'bad' : 'warn');

/** DNS TXT challenge, live "Check DNS" and removal for the organization's domains (production only). */
export function DomainVerifier(props: { orgId: string; domains: OrgDomain[]; canEdit: boolean; onChange(): void }) {
  const id = useId();
  const base = `/v1/orgs/${encodeURIComponent(props.orgId)}/domains`;
  const [domain, setDomain] = useState('');
  const [challenge, setChallenge] = useState<DomainChallenge | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const issue = useAsyncAction();
  const check = useAsyncAction();
  const remove = useAsyncAction();
  const [checking, setChecking] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const doIssue = async (d: string) => {
    const r = await issue.run(() => api<DomainChallenge>(`${base}/challenge`, { body: { domain: d } }));
    if (r) {
      setChallenge(r);
      setDomain('');
      props.onChange();
    }
  };

  const doCheck = async (d: string) => {
    setChecking(d);
    const r = await check.run(() => api<{ domain: string; status: string }>(`${base}/verify`, { body: { domain: d } }));
    setChecking(null);
    if (r) {
      setResults((m) => ({ ...m, [r.domain]: r.status }));
      if (r.status === 'verified' && challenge?.domain === r.domain) setChallenge(null);
      props.onChange();
    }
  };

  const doRemove = async (d: string) => {
    const r = await remove.run(() => api(`${base}/${encodeURIComponent(d)}`, { method: 'DELETE' }));
    setConfirmRemove(null);
    if (r) {
      if (challenge?.domain === d) setChallenge(null);
      props.onChange();
    }
  };

  return (
    <div>
      {props.domains.length > 0 && (
        <div className="p-table-wrap">
          <table className="p-table">
            <caption className="p-small p-muted" style={{ textAlign: 'left' }}>Organization domains</caption>
            <thead>
              <tr><th>Domain</th><th>Status</th><th>Used by</th><th>Verified</th><th>Last checked</th><th><span className="p-skip">Actions</span></th></tr>
            </thead>
            <tbody>
              {props.domains.map((d) => {
                const live = results[d.domain];
                return (
                  <tr key={d.domain}>
                    <td><code>{d.domain}</code><div className="p-small p-muted">and its subdomains</div></td>
                    <td>
                      <Badge tone={tone(d.status)}>{d.status}</Badge>
                      {live && live !== d.status && <> <Badge tone={tone(live)}>last check: {live}</Badge></>}
                    </td>
                    <td className="p-small">
                      {d.usedBy.length
                        ? d.usedBy.map((u) => <div key={u.serviceId}>{u.name} <span className="p-muted">· {u.uses.join(', ')}</span></div>)
                        : <span className="p-muted">—</span>}
                    </td>
                    <td>{formatTime(d.verifiedAt)}</td>
                    <td title={formatTime(d.lastCheckedAt)}>{relativeTime(d.lastCheckedAt)}</td>
                    <td>
                      {props.canEdit && (
                        <div className="p-row">
                          <button type="button" className="p-btn secondary small" disabled={checking === d.domain} onClick={() => doCheck(d.domain)}>
                            {checking === d.domain ? <Spinner label="Checking…" /> : d.status === 'verified' ? 'Re-check DNS' : 'Check DNS'}
                          </button>
                          {d.status !== 'verified' && (
                            <button type="button" className="p-btn ghost small" onClick={() => doIssue(d.domain)}>New challenge</button>
                          )}
                          {confirmRemove === d.domain ? (
                            <>
                              <button type="button" className="p-btn danger small" disabled={remove.busy} onClick={() => doRemove(d.domain)}>Confirm remove</button>
                              <button type="button" className="p-btn ghost small" onClick={() => setConfirmRemove(null)}>Cancel</button>
                            </>
                          ) : (
                            <button type="button" className="p-btn ghost small" onClick={() => setConfirmRemove(d.domain)}>Remove…</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {props.canEdit ? (
        <form
          className="p-row"
          style={{ alignItems: 'flex-end', marginTop: '1rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (domain.trim()) void doIssue(domain.trim());
          }}
        >
          <div className="p-field" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
            <label htmlFor={id}>Add a domain</label>
            <input id={id} className="p-input" placeholder="example.com" value={domain} onChange={(e) => setDomain(e.target.value)} autoComplete="off" inputMode="url" />
          </div>
          <button className="p-btn" disabled={issue.busy || !domain.trim()}>{issue.busy ? <Spinner label="Issuing…" /> : 'Get TXT challenge'}</button>
        </form>
      ) : (
        <p className="p-small p-muted">Only admins and owners can add, verify or remove domains.</p>
      )}
      <ErrorAlert error={issue.error} />
      <ErrorAlert error={check.error} />
      <ErrorAlert error={remove.error} />

      {challenge && (
        <div className="p-card muted" style={{ marginTop: '1rem' }}>
          <h3>Publish this DNS record for {challenge.domain}</h3>
          <p className="p-small">
            Add a <strong>TXT</strong> record at your DNS provider, wait for it to propagate, then press <em>Check DNS</em>.
            The challenge expires {relativeTime(challenge.expiresAt)} ({formatTime(challenge.expiresAt)}).
          </p>
          <CopyField label="Type" value={challenge.record.type} />
          <CopyField label="Name / host" value={challenge.record.name} hint="Some DNS providers want only the part before your zone, e.g. _pryme-verify" />
          <CopyField label="Value" value={challenge.record.value} hint="Exact value, including the pryme-v1= prefix. Quotes are added by most providers automatically." />
          <div className="p-row">
            <button type="button" className="p-btn" disabled={checking === challenge.domain} onClick={() => doCheck(challenge.domain)}>
              {checking === challenge.domain ? <Spinner label="Resolving DNS…" /> : 'Check DNS'}
            </button>
            {results[challenge.domain] && (
              <span role="status">
                <Badge tone={tone(results[challenge.domain]!)}>
                  {results[challenge.domain] === 'verified' ? 'Verified' : results[challenge.domain] === 'pending' ? 'Not found yet: DNS may still be propagating' : 'Failed: challenge expired or record mismatch'}
                </Badge>
              </span>
            )}
          </div>
          <p className="p-small p-muted" style={{ marginTop: '0.75rem' }}>
            Keep the record published: Pryme re-checks verified domains, and a domain whose record disappears goes stale. Wildcards are not accepted; internationalized names are stored as punycode.
          </p>
        </div>
      )}
      {!challenge && props.domains.length === 0 && (
        <Alert tone="info">
          Verify a domain once for the whole organization. Every production service of the organization can then use it and its subdomains for redirect URIs and AP2 merchant websites.
        </Alert>
      )}
    </div>
  );
}
